/**
 * Umami Analytics Utility
 *
 * Provides type-safe event tracking with username enrichment.
 * All events include the current user's username as a custom property.
 */

/**
 * Identify a user in Umami analytics
 * This must be called after login to make the user visible in the Users dashboard
 * @param {string} username - Current user's username
 * @param {Object} userData - Additional user properties (optional)
 */
export const identifyUser = (username, userData = {}) => {
  // Check if Umami is loaded
  if (typeof window.umami === 'undefined') {
    console.warn('Umami analytics not loaded');
    return;
  }

  try {
    // Identify the user with Umami
    window.umami.identify({
      username: username,
      ...userData
    });
  } catch (error) {
    console.error('Error identifying user:', error);
  }
};

/**
 * Track a custom event in Umami analytics
 * @param {string} eventName - Name of the event (e.g., 'event-rsvp', 'review-create')
 * @param {Object} eventData - Custom properties for the event
 * @param {string} username - Current user's username
 */
export const trackEvent = (eventName, eventData = {}, username = null) => {
  // Check if Umami is loaded
  if (typeof window.umami === 'undefined') {
    console.warn('Umami analytics not loaded');
    return;
  }

  // Enrich event data with username
  const enrichedData = {
    ...eventData,
    username: username || 'anonymous'
  };

  try {
    window.umami.track(eventName, enrichedData);
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

/**
 * Track page view (manual override of automatic tracking)
 * @param {string} path - Page path
 * @param {string} title - Page title
 */
export const trackPageView = (path, title) => {
  if (typeof window.umami === 'undefined') {
    return;
  }

  try {
    window.umami.track((props) => ({
      ...props,
      url: path,
      title: title
    }));
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

/**
 * Track event-related actions
 * @param {string} action - Action type (e.g., 'rsvp', 'leave', 'task-claim')
 * @param {string} eventId - Event ID
 * @param {string} username - Current user's username
 * @param {Object} additionalData - Additional event properties
 */
export const trackEventAction = (action, eventId, username, additionalData = {}) => {
  trackEvent(`event-${action}`, {
    event_id: eventId,
    ...additionalData
  }, username);
};

/**
 * Track location-related actions
 * @param {string} action - Action type (e.g., 'review-create', 'review-comment')
 * @param {string} locationId - Location ID
 * @param {string} username - Current user's username
 * @param {Object} additionalData - Additional event properties
 */
export const trackLocationAction = (action, locationId, username, additionalData = {}) => {
  trackEvent(`location-${action}`, {
    location_id: locationId,
    ...additionalData
  }, username);
};

/**
 * Track challenge-related actions
 * @param {string} action - Action type (e.g., 'join', 'resolve', 'delete')
 * @param {string} challengeId - Challenge ID
 * @param {string} username - Current user's username
 * @param {Object} additionalData - Additional event properties
 */
export const trackChallengeAction = (action, challengeId, username, additionalData = {}) => {
  trackEvent(`challenge-${action}`, {
    challenge_id: challengeId,
    ...additionalData
  }, username);
};

/**
 * Track social interactions
 * @param {string} action - Action type (e.g., 'give-kudos', 'view-profile')
 * @param {string} targetUserId - Target user ID
 * @param {string} username - Current user's username
 * @param {Object} additionalData - Additional event properties
 */
export const trackSocialAction = (action, targetUserId, username, additionalData = {}) => {
  trackEvent(`social-${action}`, {
    target_user_id: targetUserId,
    ...additionalData
  }, username);
};

/**
 * Track gamification actions
 * @param {string} action - Action type (e.g., 'badges-view', 'leaderboard-view', 'mood-set')
 * @param {string} username - Current user's username
 * @param {Object} additionalData - Additional event properties
 */
export const trackGamificationAction = (action, username, additionalData = {}) => {
  trackEvent(`gamification-${action}`, additionalData, username);
};
